// import { FcGoogle } from 'react-icons/fc';
// import { FaApple, FaFacebook } from 'react-icons/fa';
// import { IoIosArrowDown, IoMdEye, IoMdEyeOff } from 'react-icons/io';

// Asumo que las imágenes están en src/images y que pronto las añadirás.
// Si tienen otra extensión (.svg, .jpg), por favor, ajusta las importaciones.
import siwebLogo from './images/siwebLogo.png';
import hombre from './images/hombre.png';
import React, { useState, useRef, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate } from 'react-router-dom';
import CalendarScreen from './CalendarScreen';
import ThankYouScreen from './ThankYouScreen';
import ContentCreationScreen from './ContentCreationScreen';
import AdminUserListScreen from './AdminUserListScreen';
import PlanSelectionScreen from './PlanSelectionScreen';
import PreviewScreen from './PreviewScreen';

// Estilos CSS para la animación de flotación
const floatingStyles = {
  animation: 'floating 3s ease-in-out infinite',
};

// Agregar estilos CSS al head del documento
if (typeof document !== 'undefined') {
  const style = document.createElement('style');
  style.textContent = `
    @keyframes floating {
      0%, 100% {
        transform: translateY(0px);
      }
      50% {
        transform: translateY(-10px);
      }
    }
  `;
  if (!document.head.querySelector('#floating-animation')) {
    style.id = 'floating-animation';
    document.head.appendChild(style);
  }
}

const EyeIcon = ({ open }: { open: boolean }) => (
  open ? (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
  ) : (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.542-7a9.956 9.956 0 012.293-3.95m3.249-2.383A9.956 9.956 0 0112 5c4.478 0 8.268 2.943 9.542 7a9.973 9.973 0 01-4.293 5.03M15 12a3 3 0 11-6 0 3 3 0 016 0zm-6.364 6.364L19.07 4.93" /></svg>
  )
);

const LoginScreen = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isExiting, setIsExiting] = useState(false);
  const emailRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const [loginError, setLoginError] = useState('');

  // Efecto para el fade-in inicial
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoaded(true);
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  const clearEmail = () => {
    setEmail('');
    emailRef.current?.focus();
  };

  const handleLogin = async () => {
    setIsTransitioning(true);
    setLoginError('');
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });
      if (res.ok) {
        const data = await res.json();
        console.log('Datos recibidos del login:', data);
        localStorage.setItem('token', data.token);
        
        // Guardar información de múltiples cuentas si existe
        if (data.hasMultipleAccounts && data.accounts) {
          localStorage.setItem('userAccounts', JSON.stringify(data.accounts));
          localStorage.setItem('currentUser', JSON.stringify(data.user));
          // Redirigir a la pantalla de selección de plan
          setTimeout(() => {
            navigate('/select-plan');
          }, 300);
        } else {
          // Comportamiento normal para un solo usuario
          if (data.user) {
            console.log('Usuario guardado en localStorage:', data.user);
            localStorage.setItem('user', JSON.stringify(data.user));
          }
          setTimeout(() => {
            navigate('/calendar');
          }, 300);
        }
      } else {
        const error = await res.json();
        setLoginError('Email o contraseña incorrectos');
        setIsTransitioning(false);
      }
    } catch (err) {
      setLoginError('Error de red o inesperado');
      setIsTransitioning(false);
    }
  };

  return (
    <div>
      {/* Versión móvil: visible solo en pantallas menores a md */}
      <div
        className={`block md:hidden bg-[#F8F9FA] min-h-screen font-futura text-gray-800 flex flex-col items-center justify-center px-4 py-8 transition-all duration-700 ease-in-out ${isExiting ? 'opacity-0' : isLoaded ? 'opacity-100' : 'opacity-0'}`}
        style={{ pointerEvents: isLoaded && !isExiting ? 'auto' : 'none' }}
      >
        <div className="w-full max-w-md mx-auto flex flex-col items-center">
          <img src={siwebLogo} alt="Siweb Logo" className="w-32 mb-4 mt-0 ml-0 self-start" />
          <h1 className="text-3xl font-bold text-black text-left leading-tight mb-2 mt-0 w-full">
            Bienvenido al panel<br />de contenido
          </h1>
          <h2 className="text-lg font-semibold text-gray-700 text-left mb-2 w-full">Gestiona tu contenido de forma simple y rápida</h2>
          <p className="text-gray-500 text-left mb-2 text-sm w-full">
            Usa este espacio para contarnos de qué quieres hablar esta semana y comparir tus fotos.
          </p>
          <p className="text-gray-500 text-left mb-4 text-sm w-full">
            En Siweb nos encargamos del resto.
          </p>
          <div className="relative w-full flex flex-col items-center">
            {/* Efecto azulado difuminado */}
            <div className="absolute left-24 top-8 w-40 h-32 bg-gradient-to-tr from-blue-200 via-cyan-200 to-transparent opacity-40 rounded-full blur-2xl z-0" style={{ filter: 'blur(40px)' }}></div>
            <img src={hombre} alt="Personaje Siweb" className="w-48 h-auto mb-6 mt-2 relative z-10" style={floatingStyles} />
          </div>
          <div className="w-full flex flex-col gap-4">
            <div className="relative">
              <input
                ref={emailRef}
                type="email"
                placeholder="Email"
                value={email}
                onChange={e => { setEmail(e.target.value); setLoginError(''); }}
                className="w-full bg-blue-50 border-2 border-transparent focus:border-blue-300 focus:bg-white transition-colors duration-300 rounded-full py-4 px-8 pr-12 outline-none text-xl"
              />
              {email && (
                <button
                  type="button"
                  onClick={clearEmail}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  aria-label="Limpiar email"
                >
                  ×
                </button>
              )}
            </div>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                placeholder="••••••••"
                value={password}
                onChange={e => { setPassword(e.target.value); setLoginError(''); }}
                className="w-full bg-blue-50 border-2 border-transparent focus:border-blue-300 focus:bg-white transition-colors duration-300 rounded-full py-4 px-8 pr-12 outline-none text-xl"
              />
              <button
                type="button"
                onClick={() => setShowPassword(v => !v)}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                aria-label="Mostrar/ocultar contraseña"
              >
                <EyeIcon open={showPassword} />
              </button>
            </div>
            {/* Mensaje de error de login */}
            {loginError && (
              <div className="w-full mb-2 text-center bg-red-50 text-red-500 rounded-full py-3 px-4 font-semibold shadow-sm animate-fade-in">
                {loginError}
              </div>
            )}
            <div className="w-full text-center mt-0 mb-0">
              <span className="text-gray-400 text-sm"></span>
            </div>
            <button
              className="w-full bg-white border-2 border-blue-300 hover:bg-blue-50 transition-all duration-300 text-blue-500 font-bold py-4 px-8 rounded-full text-xl"
              onClick={handleLogin}
              disabled={isTransitioning}
            >
              {isTransitioning ? 'Accediendo...' : 'Acceder'}
            </button>
          </div>
        </div>
      </div>
      {/* Versión escritorio: visible solo en md+ */}
      <div className="hidden md:block">
        {/* Copia aquí el diseño original de escritorio */}
        <div 
          className="bg-[#F8F9FA] min-h-screen font-futura text-gray-800 relative overflow-hidden transition-all duration-500 ease-in-out"
          style={{ 
            opacity: isTransitioning ? 0 : isLoaded ? 1 : 0,
            transform: isTransitioning ? 'translateY(-32px)' : isLoaded ? 'translateY(0)' : 'translateY(32px)',
            overflow: 'hidden'
          }}
        >
          {/* Language Selector */}
          <div className="absolute top-6 right-8 z-30">
            <button className="flex items-center space-x-2 text-sm">
              <span></span>
              {/* Icono de flecha hacia abajo aquí */}
            </button>
          </div>
          <div className="container mx-auto h-screen grid lg:grid-cols-2 items-center gap-x-24 px-0">
            {/* Left Column */}
            <div className="flex flex-col justify-center items-start pr-2 ml-0">
              <img src={siwebLogo} alt="Siweb Logo" className="w-44 mb-28 ml-0" />
              <h1 className="text-6xl lg:text-7xl font-futura-heavy font-bold leading-tight mb-6 tracking-widest text-black ml-0">
                Bienvenido al<br />panel de<br />contenido
              </h1>
              <h2 className="text-2xl lg:text-3xl font-semibold text-gray-600 mb-8 max-w-xs lg:max-w-sm ml-0">
                Gestiona tu contenido de forma simple y rápida
              </h2>
              <p className="text-gray-500 max-w-md mb-2 text-sm ml-0">
                Usa este espacio para contarnos de qué quieres hablar esta semana y compartir tus fotos.
              </p>
              <p className="text-gray-500 max-w-md text-sm ml-0">
                En Siweb nos encargamos del resto.
              </p>
            </div>
            {/* Right Column - Form */}
            <div className="flex justify-center items-center relative z-20 ml-auto -mt-12">
              <div className="w-full px-4 py-6 flex flex-col items-center scale-[1.05]" style={{ maxWidth: '600px' }}>
                <div className="relative mb-6 w-full">
                  <input
                    ref={emailRef}
                    type="email"
                    placeholder="Email"
                    value={email}
                    onChange={e => { setEmail(e.target.value); setLoginError(''); }}
                    className="w-full bg-blue-50 border-2 border-transparent focus:border-blue-300 focus:bg-white transition-colors duration-300 rounded-full py-4 px-8 pr-12 outline-none text-xl"
                  />
                  {email && (
                    <button
                      type="button"
                      onClick={clearEmail}
                      className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      aria-label="Limpiar email"
                    >
                      ×
                    </button>
                  )}
                </div>
                <div className="relative mb-4 w-full">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={password}
                    onChange={e => { setPassword(e.target.value); setLoginError(''); }}
                    className="w-full bg-blue-50 border-2 border-transparent focus:border-blue-300 focus:bg-white transition-colors duration-300 rounded-full py-4 px-8 pr-12 outline-none text-xl"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(v => !v)}
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    aria-label="Mostrar/ocultar contraseña"
                  >
                    <EyeIcon open={showPassword} />
                  </button>
                </div>
                {loginError && (
                  <div className="w-full mb-4 text-center bg-red-50 text-red-500 rounded-full py-3 px-4 font-semibold shadow-sm animate-fade-in">
                    {loginError}
                  </div>
                )}
                <button
                  className={`w-full bg-white border-2 border-blue-300 hover:bg-blue-50 transition-all duration-300 text-blue-500 font-bold py-4 px-8 rounded-full text-xl ${isTransitioning ? 'scale-95 opacity-75' : 'hover:scale-105'}`}
                  onClick={handleLogin}
                  disabled={isTransitioning}
                >
                  {isTransitioning ? 'Accediendo...' : 'Acceder'}
                </button>
              </div>
            </div>
          </div>
          {/* Floating Man Image */}
          <div className="absolute left-[38%] top-1/2 transform -translate-y-1/2 pointer-events-none z-10 hidden lg:block">
            <div 
              className="absolute left-20 top-24 inset-0 -m-32 bg-gradient-to-tr from-blue-200 via-cyan-200 to-transparent opacity-30 rounded-full blur-3xl" 
              style={{ filter: 'blur(100px)'}}
            ></div>
            <img src={hombre} alt="" className="w-auto h-auto max-w-xl max-h-[70vh] relative" style={floatingStyles} />
          </div>
        </div>
      </div>
    </div>
  );
};

// Componente para verificar autenticación
const AuthGuard = ({ children }: { children: React.ReactNode }) => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/');
      return;
    }
    setIsLoading(false);
  }, [navigate]);

  if (isLoading) {
    return <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#44aaf6] mx-auto mb-4"></div>
        <p className="text-gray-600">Verificando autenticación...</p>
      </div>
    </div>;
  }

  return <>{children}</>;
};

// Componente wrapper para la pantalla de selección de plan
const PlanSelectionWrapper = () => {
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState<any[]>([]);

  useEffect(() => {
    const userAccounts = localStorage.getItem('userAccounts');
    if (!userAccounts) {
      // Si no hay cuentas múltiples, redirigir al calendario
      navigate('/calendar');
      return;
    }

    try {
      const parsedAccounts = JSON.parse(userAccounts);
      setAccounts(parsedAccounts);
    } catch (error) {
      console.error('Error parsing user accounts:', error);
      navigate('/calendar');
    }
  }, [navigate]);

  const handlePlanSelect = async (accountId: number) => {
    // Encontrar la cuenta seleccionada
    const selectedAccount = accounts.find(acc => acc.id === accountId);
    if (selectedAccount) {
      try {
        // Generar nuevo token para la cuenta seleccionada
        const token = localStorage.getItem('token');
        const res = await fetch(`${process.env.REACT_APP_API_URL}/generate-token`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ accountId })
        });
        
        if (res.ok) {
          const data = await res.json();
          // Actualizar el token y la información del usuario
          localStorage.setItem('token', data.token);
          localStorage.setItem('user', JSON.stringify(data.user));
          // Limpiar las cuentas múltiples
          localStorage.removeItem('userAccounts');
          localStorage.removeItem('currentUser');
          // Redirigir al calendario
          navigate('/calendar');
        } else {
          console.error('Error al generar token para la cuenta seleccionada');
          // Fallback: usar el método anterior
          localStorage.setItem('user', JSON.stringify(selectedAccount));
          localStorage.removeItem('userAccounts');
          localStorage.removeItem('currentUser');
          navigate('/calendar');
        }
      } catch (error) {
        console.error('Error de red al generar token:', error);
        // Fallback: usar el método anterior
        localStorage.setItem('user', JSON.stringify(selectedAccount));
        localStorage.removeItem('userAccounts');
        localStorage.removeItem('currentUser');
        navigate('/calendar');
      }
    }
  };

  if (accounts.length === 0) {
    return <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#44aaf6] mx-auto mb-4"></div>
        <p className="text-gray-600">Cargando planes...</p>
      </div>
    </div>;
  }

  return <PlanSelectionScreen accounts={accounts} onPlanSelect={handlePlanSelect} />;
};

const App = () => {
  return (
    <Router>
      <Routes>
        <Route path="/" element={<LoginScreen />} />
        <Route path="/admin-users" element={<AuthGuard><AdminUserListScreen /></AuthGuard>} />
        <Route path="/select-plan" element={<AuthGuard><PlanSelectionWrapper /></AuthGuard>} />
        <Route path="/calendar" element={<AuthGuard><CalendarScreen /></AuthGuard>} />
        <Route path="/thank-you" element={<ThankYouScreen />} />
        <Route path="/create-content" element={<AuthGuard><ContentCreationScreen /></AuthGuard>} />
        <Route path="/preview" element={<AuthGuard><PreviewScreen /></AuthGuard>} />
        <Route path="/preview/:uuid" element={<PreviewScreen />} />
      </Routes>
    </Router>
  );
};

export default App;
